import React, { useState, useEffect } from "react";
import { useAuth } from "../../../components/context/AuthProvider";
import { apiFetch } from "../../../services/apiClient";
import { Button, Input, LoadingState, ErrorBoundary, useToast } from "../../../components/ui";
import { useFormValidation } from "../../../hooks";
import { commonValidationRules } from "../../../utils/validation";
import { useWalkthrough } from "../../../contexts/WalkthroughContext";

interface UserDetailsForm {
  username: string;
  email: string;
  name: string;
}

interface PasswordChangeForm {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const UserProfile: React.FC = () => {
  const auth = useAuth();
  const toast = useToast();
  const { startTour } = useWalkthrough();
  const [loading, setLoading] = useState(true);

  // User details form
  const detailsForm = useFormValidation<UserDetailsForm>({
    initialValues: {
      username: "",
      email: "",
      name: "",
    },
    validationRules: {
      username: commonValidationRules.username,
      email: commonValidationRules.email,
      name: commonValidationRules.name,
    },
    onSubmit: async (values) => {
      try {
        const response = await apiFetch("/api/protected/user/profile", {
          method: "PUT",
          body: JSON.stringify(values),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          const errorMsg = errorData?.message || "Failed to update details";
          toast.showToast({ type: "error", message: errorMsg });
          throw new Error(errorMsg);
        }
        toast.showToast({ type: "success", message: "Details updated successfully!" });
        fetchUserProfile();
      } catch (error) {
        console.error("Error saving details:", error);
        toast.showToast({
          type: "error",
          message: (error as Error)?.message || "Failed to update details.",
        });
        throw new Error("Failed to update details.");
      }
    },
  });

  // Password change form
  const passwordForm: ReturnType<typeof useFormValidation<PasswordChangeForm>> =
    useFormValidation<PasswordChangeForm>({
      initialValues: {
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      },
      validationRules: {
        currentPassword: commonValidationRules.required,
        newPassword: commonValidationRules.password,
        confirmPassword: {
          required: true,
          custom: (value: unknown, allValues?: Record<string, unknown>): string | null =>
            value !== allValues?.newPassword ? "Passwords do not match" : null,
        },
      },
      onSubmit: async (values) => {
        try {
          const response = await apiFetch("/api/protected/user/change-password", {
            method: "PUT",
            body: JSON.stringify(values),
          });

          if (!response.ok) {
            throw new Error("Failed to change password");
          }
          toast.showToast({ type: "success", message: "Password changed successfully!" });
          passwordForm.resetForm();
        } catch (error) {
          console.error("Error changing password:", error);
          throw new Error("Failed to change password.");
        }
      },
    });

  async function fetchUserProfile() {
    try {
      const response = await apiFetch("/api/protected/user/profile");

      if (!response.ok) {
        throw new Error("Failed to fetch profile");
      }

      const data = await response.json();
      detailsForm.setValues({
        email: data.email,
        name: data.name,
        username: data.username,
      });
    } catch (error) {
      console.error("Error fetching profile:", error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchUserProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth]);

  if (loading) {
    return <LoadingState message="Loading profile..." size="md" fullHeight />;
  }

  return (
    <ErrorBoundary errorMessage="There was an error loading your profile. Please try refreshing the page.">
      <div className="p-8 bg-zinc-950 text-white min-h-screen">
        <h1 className="text-2xl font-light tracking-wide mb-8">Weaver Profile</h1>
        {/* Walkthrough */}
        <section className="bg-zinc-900 border border-zinc-800 p-6 mb-8">
          <h2 className="text-sm font-semibold text-amber-400 tracking-wide mb-2">Platform Tour</h2>
          <p className="text-zinc-400 text-sm mb-4">
            Restart the interactive walkthrough to rediscover the main features of RoleWeave.
          </p>
          <Button type="button" variant="primary" size="md" onClick={startTour}>
            Start Walkthrough
          </Button>
        </section>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Edit User Details */}
          <section className="bg-zinc-900 border border-zinc-800 p-6">
            <h2 className="text-sm font-semibold text-amber-400 tracking-wide mb-4">
              Edit Details
            </h2>
            <form onSubmit={detailsForm.handleSubmit}>
              <div className="mb-4">
                <Input
                  type="text"
                  id="username"
                  name="username"
                  label="Username"
                  disabled
                  value={detailsForm.values.username}
                  fullWidth
                  variant="filled"
                />
              </div>
              <div className="mb-4">
                <Input
                  type="email"
                  id="email"
                  name="email"
                  label="Email"
                  value={detailsForm.values.email}
                  onChange={detailsForm.handleChange("email")}
                  error={detailsForm.errors.email}
                  fullWidth
                  variant="filled"
                />
              </div>
              <div className="mb-4">
                <Input
                  type="text"
                  id="name"
                  name="name"
                  label="Name"
                  value={detailsForm.values.name}
                  onChange={detailsForm.handleChange("name")}
                  error={detailsForm.errors.name}
                  fullWidth
                  variant="filled"
                />
              </div>
              <Button type="submit" variant="primary" size="md" disabled={detailsForm.isSubmitting}>
                {detailsForm.isSubmitting ? "Saving..." : "Save Details"}
              </Button>
            </form>
          </section>

          {/* Change Password */}
          <section className="bg-zinc-900 border border-zinc-800 p-6">
            <h2 className="text-sm font-semibold text-amber-400 tracking-wide mb-4">
              Change Password
            </h2>
            <form onSubmit={passwordForm.handleSubmit}>
              <div className="mb-4">
                <Input
                  type="password"
                  id="currentPassword"
                  name="currentPassword"
                  label="Current Password"
                  value={passwordForm.values.currentPassword}
                  onChange={passwordForm.handleChange("currentPassword")}
                  error={passwordForm.errors.currentPassword}
                  fullWidth
                  variant="filled"
                />
              </div>
              <div className="mb-4">
                <Input
                  type="password"
                  id="newPassword"
                  name="newPassword"
                  label="New Password"
                  value={passwordForm.values.newPassword}
                  onChange={passwordForm.handleChange("newPassword")}
                  error={passwordForm.errors.newPassword}
                  fullWidth
                  variant="filled"
                />
              </div>
              <div className="mb-4">
                <Input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  label="Confirm New Password"
                  value={passwordForm.values.confirmPassword}
                  onChange={passwordForm.handleChange("confirmPassword")}
                  error={passwordForm.errors.confirmPassword}
                  fullWidth
                  variant="filled"
                />
              </div>
              <Button
                type="submit"
                variant="primary"
                size="md"
                disabled={passwordForm.isSubmitting}
              >
                {passwordForm.isSubmitting ? "Changing..." : "Change Password"}
              </Button>
            </form>
          </section>
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default UserProfile;
